/**
 * Unit rendering helpers — used by UnitsRender (SPEC 4.8) until AthleteProfile
 * provides per-user preference. v1: reads from prefs store.
 *
 * v2: respects AthleteProfile.units_preference + locale-aware separators.
 */

const KG_PER_LB = 0.453592;
const KM_PER_MI = 1.60934;
const C_TO_F = (c: number) => c * (9 / 5) + 32;

export type Quantity =
  | { kind: "weight"; kg: number }
  | { kind: "distance"; km: number }
  | { kind: "temperature"; c: number }
  | { kind: "pace"; sPerKm: number }
  | { kind: "kcal"; kcal: number }
  | { kind: "duration"; minutes: number };

export function formatQuantity(
  q: Quantity,
  pref: "metric" | "imperial" | "metric_with_imperial_secondary",
): { primary: string; secondary?: string } {
  switch (q.kind) {
    case "weight":
      return formatWeight(q.kg, pref);
    case "distance":
      return formatDistance(q.km, pref);
    case "temperature":
      return formatTemperature(q.c, pref);
    case "pace":
      return formatPace(q.sPerKm, pref);
    case "kcal":
      return { primary: `${Math.round(q.kcal)} kcal` };
    case "duration":
      return { primary: formatDuration(q.minutes) };
  }
}

function formatWeight(
  kg: number,
  pref: "metric" | "imperial" | "metric_with_imperial_secondary",
) {
  const kgStr = `${kg.toFixed(1)} kg`;
  const lbStr = `${(kg / KG_PER_LB).toFixed(1)} lb`;
  if (pref === "metric") return { primary: kgStr };
  if (pref === "imperial") return { primary: lbStr };
  return { primary: kgStr, secondary: lbStr };
}

function formatDistance(
  km: number,
  pref: "metric" | "imperial" | "metric_with_imperial_secondary",
) {
  const kmStr = `${km.toFixed(2)} km`;
  const miStr = `${(km / KM_PER_MI).toFixed(2)} mi`;
  if (pref === "metric") return { primary: kmStr };
  if (pref === "imperial") return { primary: miStr };
  return { primary: kmStr, secondary: miStr };
}

function formatTemperature(
  c: number,
  pref: "metric" | "imperial" | "metric_with_imperial_secondary",
) {
  const cStr = `${Math.round(c)}°C`;
  const fStr = `${Math.round(C_TO_F(c))}°F`;
  if (pref === "metric") return { primary: cStr };
  if (pref === "imperial") return { primary: fStr };
  return { primary: cStr, secondary: fStr };
}

function formatPace(
  sPerKm: number,
  pref: "metric" | "imperial" | "metric_with_imperial_secondary",
) {
  const kmStr = paceMmSs(sPerKm) + " /km";
  const miStr = paceMmSs(sPerKm * KM_PER_MI) + " /mi";
  if (pref === "metric") return { primary: kmStr };
  if (pref === "imperial") return { primary: miStr };
  return { primary: kmStr, secondary: miStr };
}

function paceMmSs(sPerUnit: number): string {
  const mm = Math.floor(sPerUnit / 60);
  const ss = Math.round(sPerUnit % 60);
  return `${mm}:${ss.toString().padStart(2, "0")}`;
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return `${h}h${m ? ` ${m}m` : ""}`;
}
